import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, tap } from 'rxjs';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  ProfilelistActions,
  createProfilelistCloneEffects,
  createProfilelistDataEffects,
  createProfilelistDrilldownEffects,
  createProfilelistEditEffects,
  profilelistFeature,
} from './profilelist.feature';
import { Profile } from 'src/app/shared/entities/profile';
import { FetchingFn } from 'src/app/shared/store/grid-data/grid-data.types';
import { LangService } from 'src/app/shared/services/lang/lang.service';
import { ToastrService } from 'src/app/shared/services/toastr/toastr-service';
import { ApiService } from 'src/app/shared/services/api/api.service';
import { ProfileDataService } from 'src/app/shared/store/features/profile-data/profile-data.service';
import { stringComparator } from 'src/app/shared/util/comparators';
import { extractErrorMessage } from 'src/app/shared/util/extract-error-message';
import { GridDataSourceState } from 'src/app/shared/store/grid-data/grid-data-source-state';
import { pageDataToGridData } from 'src/app/shared/store/grid-data/page-data-to-grid-data';

@Injectable()
export class ProfilelistEffects {
  private action$ = inject(Actions);
  private router = inject(Router);
  private store = inject(Store);
  private langService = inject(LangService);
  private labels = this.langService.lang.features.profilelist;
  private toastrService = inject(ToastrService);
  private apiService = inject(ApiService);
  private profilelistDataService = inject(ProfileDataService);

  private gridDataSourceState$: Observable<GridDataSourceState<Profile>> =
    pageDataToGridData<Profile>({
      filterChecks: {
        profileName: (profile, filter) =>
          (profile.profileName ?? '')
            .toLowerCase()
            .includes(filter.toLowerCase()),
      },
      comparisons: {
        profileName: stringComparator(profile => profile.profileName ?? ''),
        profileType: stringComparator(profile => profile.profileType ?? ''),
      },
    })(
      this.profilelistDataService.state$,
      this.store.select(profilelistFeature.selectGridDataParams),
    );

  private dataEffects = createProfilelistDataEffects(
    this.action$,
    this.store,
    this.gridDataSourceState$,
    this.router,
    {
      requestData: () => this.profilelistDataService.ensure(),
      refreshData: () => {
        /* Do nothing: refresh not supported */
      },
    },
  );

  private drilldownEffects = createProfilelistDrilldownEffects(
    this.action$,
    this.router,
  );

  private editEffects = createProfilelistEditEffects(
    this.action$,
    this.router,
    {
      saveItem: (profile, dto) =>
        this.apiService
          .updateProfile(profile.profileName ?? '', dto)
          .pipe(
            tap(updatedProfile =>
              this.profilelistDataService.transform(profiles =>
                profiles.map(profile =>
                  profile.profileName === updatedProfile.profileName
                    ? updatedProfile
                    : profile,
                ),
              ),
            ),
          ),
      notifyFail: (_, __, error) =>
        this.toastrService.error(
          extractErrorMessage(error) || this.labels.toasts.updateError,
        ),
    },
  );

  private cloneEffects = createProfilelistCloneEffects(
    this.action$,
    this.router,
    {
      saveItem: (_profile, dto) =>
        this.apiService.createProfile(dto).pipe(
          tap(() => {
            this.profilelistDataService.clear();
            this.profilelistDataService.ensure();
          }),
        ),
      notifyFail: (_, __, error) =>
        this.toastrService.error(
          extractErrorMessage(error) || this.labels.toasts.cloneError,
        ),
    },
  );

  dataRequest$ = this.dataEffects.dataRequest$;
  dataUpdated$ = this.dataEffects.dataUpdated$;
  navigated$ = this.dataEffects.navigated$;
  refresh$ = this.dataEffects.refresh$;
  applyFilter$ = this.dataEffects.applyFilter$;
  applySorting$ = this.dataEffects.applySorting$;
  drilldown$ = this.drilldownEffects.drilldown$;
  backfromDrilldown$ = this.drilldownEffects.backFromDrilldown$;
  cancelEdit$ = this.editEffects.cancel$;
  doCancelEdit$ = this.editEffects.doCancel$;
  edit$ = this.editEffects.edit$;
  saveEdit$ = this.editEffects.save$;
  saveFail$ = this.editEffects.saveFail$;
  saveSuccess$ = this.editEffects.saveSuccess$;
  cancelClone$ = this.cloneEffects.cancel$;
  doCancelClone$ = this.cloneEffects.doCancel$;
  clone$ = this.cloneEffects.edit$;
  saveClone$ = this.cloneEffects.save$;
  saveCloneFail$ = this.cloneEffects.saveFail$;
  saveCloneSuccess$ = this.cloneEffects.saveSuccess$;

  toastOnError$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(ProfilelistActions.dataLoadFailure),
        tap(action =>
          this.toastrService.error(
            extractErrorMessage(action.error) ||
              this.labels.toasts.fetchingError,
          ),
        ),
      ),
    { dispatch: false },
  );

  reloadProfileList$ = createEffect(() =>
    this.action$.pipe(
      ofType(ProfilelistActions.reload),
      tap(() => this.profilelistDataService.ensure()),
    ),
  );
}
